import type { BrowseFilter, CampaignType, List, FilterRange, Platform, Talent } from "@/shared/types"
import { BROWSE_PLATFORMS, getEligibilityFromBrowseFilter } from "@/utils/browseFilter"
import { deepEqual } from "@/utils/core"
import { getDefaultGuidePrice } from "@/utils/prices"
import { talentMatchesPitchingEligibility } from "@/utils/pitching/eligibility"

const DEFAULT_PCT_RANGE = { lower: 0, upper: 100 }

export function countActiveFilters(value: BrowseFilter, showPriceChips: boolean): number {
    let count = 0
    if (value.countries?.length) count += 1
    if (value.cities?.length) count += 1
    if (value.gender?.length) count += 1
    if (value.contentLanguage?.length) count += 1
    if (value.favoriteGenre?.length) count += 1
    if (value.contentTags?.length) count += 1
    if (value.topFollowerCountries?.length) count += 1
    if (value.topFollowerAgeBrackets?.length) count += 1
    if (value.emailInBio && value.emailInBio !== "Any") count += 1
    if (value.tagFilterLogic === "AND" && value.contentTags?.length) count += 1
    if (!deepEqual(value.femaleFollowerPctRange ?? DEFAULT_PCT_RANGE, DEFAULT_PCT_RANGE)) count += 1
    if (!deepEqual(value.maleFollowerPctRange ?? DEFAULT_PCT_RANGE, DEFAULT_PCT_RANGE)) count += 1

    for (const platform of BROWSE_PLATFORMS) {
        if (rangeIsActive(value[platform]?.followersRange)) count += 1
        if (showPriceChips && rangeIsActive(value[platform]?.priceRange)) count += 1
    }
    return count
}

/** Empty platforms still match; pages that need “choose a platform” empty the list themselves. */
export function filterTalent(args: {
    talent: Talent[]
    filters: BrowseFilter
    search: string
    listId: string
    lists: List[]
    platforms: Platform[]
    campaignType: CampaignType
}): Talent[] {
    const { talent, filters, search, listId, lists, platforms, campaignType } = args
    const cleanSearch = search.replace("@", "").toLocaleLowerCase()
    const selectedList = listId ? lists.find((list) => list.id === listId) : undefined

    return talent.filter((tal) => {
        if (!talentMatchesPitchingEligibility(tal, getEligibilityFromBrowseFilter(filters), platforms)) {
            return false
        }
        if (!matchesPriceChips(tal, filters, campaignType)) {
            return false
        }
        if (cleanSearch.length) {
            const tt = tal.platformData?.TikTok?.username?.toLocaleLowerCase()
            const ig = tal.platformData?.Instagram?.username?.toLocaleLowerCase()
            const yt = tal.platformData?.YouTube?.username?.toLocaleLowerCase()
            const handleMatch = Boolean(
                tt?.includes(cleanSearch) || ig?.includes(cleanSearch) || yt?.includes(cleanSearch),
            )
            if (!handleMatch && search !== tal.id) {
                return false
            }
        }
        if (listId && selectedList?.talent && !selectedList.talent[tal.id]) {
            return false
        }
        return true
    })
}

function rangeIsActive(range: FilterRange | undefined | null): boolean {
    return range != null && (range.lower != null || range.upper != null)
}

function matchesPriceChips(
    talent: Talent,
    filters: BrowseFilter,
    campaignType: CampaignType,
): boolean {
    const active = BROWSE_PLATFORMS.filter((platform) => rangeIsActive(filters[platform]?.priceRange))
    if (!active.length) {
        return true
    }
    return active.every((platform) => {
        const price = getDefaultGuidePrice(talent.localPrices, platform, campaignType)
        const range = filters[platform]!.priceRange!
        if (typeof price !== "number") {
            return false
        }
        const minOk = range.lower == null || range.lower <= price
        const maxOk = range.upper == null || price <= range.upper
        return minOk && maxOk
    })
}
